/**
 * loggingHelpers.js
 *
 * US-001-R2A: Pure helper functions for gamelog accordion headers.
 *
 * Exports:
 * - getTakersForHand(handStep) - extracts all "Take" actions from a hand
 * - formatHandSummary(handStep) - formats accordion header string
 */

/**
 * Extracts amount from a Description string like "Took $44"
 * @param {string} description
 * @returns {number} - extracted amount or 0
 */
function parseAmountFromDescription(description) {
  if (!description) return 0;
  const match = description.match(/took\s*\$(\d+)/i);
  return match ? parseInt(match[1], 10) : 0;
}

/**
 * Extracts all "Take" actions from a hand's BettingRounds.
 * Searches both BeforeRoundSteps and AfterRoundSteps.
 *
 * @param {object} handStep - { HandId, BettingRounds: [...] }
 * @returns {Array<{name: string, amount: number}>}
 */
export function getTakersForHand(handStep) {
  const takers = [];

  if (!handStep || !handStep.BettingRounds) {
    return takers;
  }

  for (const round of handStep.BettingRounds) {
    const allSteps = [
      ...(round.BeforeRoundSteps || []),
      ...(round.AfterRoundSteps || []),
    ];

    for (const step of allSteps) {
      if (step.Action === "Take") {
        const amount =
          step.Amount > 0
            ? step.Amount
            : parseAmountFromDescription(step.Description);

        takers.push({
          name: step.PlayerName,
          amount,
        });
      }
    }
  }

  return takers;
}

/**
 * Formats an accordion header for a hand.
 *
 * Examples:
 * - Single taker: "6) 25=>Adam"
 * - Split pot: "9) 25=>Split A & T" (initials sorted alphabetically by name)
 * - In progress: "10) (in progress)"
 *
 * @param {object} handStep - { HandId, BettingRounds: [...] }
 * @returns {string}
 */
export function formatHandSummary(handStep) {
  const n = (handStep.HandId ?? 0) + 1;
  const takers = getTakersForHand(handStep);

  if (takers.length === 0) {
    return `${n}) (in progress)`;
  }

  const total = takers.reduce((sum, t) => sum + t.amount, 0);

  if (takers.length === 1) {
    return `${n}) ${total}=>${takers[0].name}`;
  }

  // Multiple takers (split pot)
  // Sort by name alphabetically, then take first char of each name
  const sortedTakers = [...takers].sort((a, b) =>
    a.name.localeCompare(b.name)
  );
  const initials = sortedTakers
    .map((t) => t.name.charAt(0).toUpperCase())
    .join(" & ");

  return `${n}) ${total}=>Split ${initials}`;
}
